if (!customElements.get('recently-viewed')) {
  class RecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'meshki:recently-viewed';
      this.maxHistory = 20;
    }

    connectedCallback() {
      // Recently viewed persisted in localStorage, only enable with functionality
      // consent. Read via shared Pandectes helper (@see assets/pandectes-consent.js)
      // If helper is missing, fail closed (treat as no consent).
      if (!window.themeConsent || !window.themeConsent.functionalityGranted()) {
        // No consent: remove section from DOM. Re-enables from next navigation
        // when consent granted.
        this.closest('.section')?.remove();
        return;
      }

      this.init();
    }

    init() {
      // Always record the current product so history builds even if this
      // section is never scrolled into view. On non-product pages the handle
      // is blank and nothing is recorded.
      const currentHandle = this.dataset.currentHandle;
      const history = this.record(currentHandle);

      // Products to display: most-recent first, excluding the current one.
      const limit = parseInt(this.dataset.limit, 10) || 4;
      this.minimum = parseInt(this.dataset.minimum, 10) || 1;
      const viewable = history.filter((handle) => handle !== currentHandle);
      this.handles = viewable.slice(0, limit);

      // Don't render below the configured minimum number of viewable products.
      if (viewable.length < this.minimum) {
        this.closest('.section').remove();
        return;
      }

      // Lazy-load the cards, mirroring <product-recommendations>.
      const onIntersect = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.renderCards();
      };
      new IntersectionObserver(onIntersect.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }

    read() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.storageKey));
        return Array.isArray(stored) ? stored : [];
      } catch (e) {
        return [];
      }
    }

    record(handle) {
      let history = this.read();
      if (handle) {
        history = history.filter((h) => h !== handle);
        history.unshift(handle);
        history = history.slice(0, this.maxHistory);
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(history));
        } catch (e) {
          /* storage full / unavailable — non-fatal */
        }
      }
      return history;
    }

    renderCards() {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      const requests = this.handles.map((handle) =>
        fetch(root + 'products/' + handle + '?view=recently-viewed-card')
          .then((response) => {
            if (!response.ok || response.redirected) return null;
            return response.text();
          })
          .then((text) => {
            if (!text) return null;
            const card = new DOMParser().parseFromString(text, 'text/html').querySelector('product-card');
            return card ? card.outerHTML : null;
          })
          .catch(() => null),
      );

      Promise.all(requests).then((cards) => {
        const valid = cards.filter((card) => card);
        if (valid.length < this.minimum) {
          this.closest('.section').remove();
          return;
        }
        this.innerHTML = this.buildMarkup(valid);
      });
    }

    buildMarkup(cards) {
      const d = this.dataset;
      const id = d.sectionId;
      const gapless = d.gaplessGrid === 'true';
      const sliderMobile = d.sliderMobile === 'true';
      const sliderDesktop = d.sliderDesktop === 'true';

      const slides = cards
        .map(
          (card, i) => '<li class="grid__item" id="Slide-' + (i + 1) + '-RecentlyViewed-' + id + '">' + card + '</li>',
        )
        .join('');

      let gridClasses =
        'scrollbar-hide grid grid--peek grid--1-col-tablet-down product-grid grid--' +
        d.columnsDesktop +
        '-col-desktop grid--' +
        d.columnsTablet +
        '-col-tablet grid--' +
        d.columnsMobile +
        '-col';
      if (gapless) gridClasses += ' grid--gapless';
      if (cards.length < 3) gridClasses += ' grid--medium-center';
      if (cards.length < 4) gridClasses += ' grid--large-up-center';
      if (sliderMobile) gridClasses += ' slider slider--mobile';
      if (sliderDesktop) gridClasses += ' slider slider--tablet slider--desktop';

      let carousel =
        '<ul id="Slider-RecentlyViewed-' + id + '" class="' + gridClasses + '" role="list">' + slides + '</ul>';

      if (sliderMobile || sliderDesktop) {
        let sliderClasses = 'slider-mobile-gutter slider-component-full-width slider-component-desktop';
        if (gapless) sliderClasses += ' slider-component-gapless';
        carousel =
          '<draggable-slider-component class="' +
          sliderClasses +
          '">' +
          carousel +
          '<div class="slider__progress-wrap margin-sm"><span class="slider__progress-track"><span class="slider__progress-bar"></span></span></div>' +
          '</draggable-slider-component>';
      }

      let heading = '';
      if (d.heading) {
        heading =
          '<div class="title-wrapper--lg center">' +
          '<p class="lh-zero related-products__heading inline-richtext ' +
          d.headingSize +
          '">' +
          d.heading +
          '</p>' +
          '</div>';
      }

      return heading + carousel;
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
}
